import React, { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./MainScreenStyles.css";
import DailyWords from "./DailyWords/DailyWords";

import {
  FaApple,
  FaBookOpen,
  FaGooglePlay,
  FaGraduationCap,
} from "react-icons/fa";

const IOS_STORE_URL =
  "https://apps.apple.com/de/app/learn-german-a1-c2/id6746383285?l=en-GB";

const ANDROID_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.kugathaj.deutsch";

const createQrUrl = (url: string): string => {
  return `https://api.qrserver.com/v1/create-qr-code/?size=520x520&margin=16&data=${encodeURIComponent(
    url,
  )}`;
};

const normalizeRoute = (route: string): string => {
  const cleanRoute = String(route || "/").trim();

  if (!cleanRoute || cleanRoute === "/") {
    return "/";
  }

  return cleanRoute.startsWith("/") ? cleanRoute : `/${cleanRoute}`;
};

type LearningCard = {
  key: string;
  className: string;
  route: string;
  aria: string;
  title: string;
  subtitle: string;
  description: string;
  icon: React.ReactNode;
};

type AppQrCardProps = {
  platform: string;
  device: string;
  storeUrl: string;
  variant: "ios" | "android";
  icon: React.ReactNode;
};

const learningCards: LearningCard[] = [
  {
    key: "course",
    className: "main-card-course",
    route: "/courses",
    aria: "Kursseite öffnen",
    title: "Kurs",
    subtitle: "ONLINE DEUTSCHKURS",
    description: "Strukturierter Unterricht von Anfänger bis Fortgeschritten.",
    icon: <FaGraduationCap />,
  },
  {
    key: "books",
    className: "main-card-books",
    route: "/books",
    aria: "Bücherseite öffnen",
    title: "Bücher",
    subtitle: "60 DEUTSCHE BÜCHER",
    description: "Schritt-für-Schritt Bücher für jedes Deutschniveau.",
    icon: <FaBookOpen />,
  },
];

const AppQrCard: React.FC<AppQrCardProps> = ({
  platform,
  device,
  storeUrl,
  variant,
  icon,
}) => {
  return (
    <article
      className={`main-app-qr-card main-app-qr-card-${variant}`}
      aria-label={`${platform} App herunterladen`}
    >
      <div className="main-app-qr-color-glow" aria-hidden="true" />

      <div className="main-app-qr-card-top">
        <span
          className={`main-app-qr-platform main-app-qr-platform-${variant}`}
        >
          {icon}
          {platform}
        </span>

        <span className="main-app-qr-device">{device}</span>
      </div>

      <a
        href={storeUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="main-app-qr-link"
        aria-label={`${platform} App in neuem Tab öffnen`}
      >
        <span className="main-app-qr-frame">
          <img
            src={createQrUrl(storeUrl)}
            alt={`${platform} QR-Code`}
            className="main-app-qr-image"
            loading="lazy"
          />
        </span>
      </a>
    </article>
  );
};

const MainScreen: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    document.documentElement.classList.add("main-screen-page");
    document.body.classList.add("main-screen-page");

    return () => {
      document.documentElement.classList.remove("main-screen-page");
      document.body.classList.remove("main-screen-page");
    };
  }, []);

  const handleCardClick = useCallback(
    (route: string): void => {
      const nextRoute = normalizeRoute(route);

      window.dispatchEvent(
        new CustomEvent("dear-language-route-change", {
          detail: {
            pathname: nextRoute,
            search: "",
          },
        }),
      );

      if (nextRoute === "/courses") {
        window.dispatchEvent(new CustomEvent("dear-language-course-reset"));
        window.dispatchEvent(new CustomEvent("dear-language-course-page-opened"));
      }

      if (nextRoute === "/books") {
        window.dispatchEvent(new CustomEvent("dear-language-books-page-opened"));
      }

      navigate(nextRoute, {
        replace: false,
      });

      window.requestAnimationFrame(() => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: "auto",
        });
      });
    },
    [navigate],
  );

  return (
    <main className="main-home-container" aria-label="Startseite">
      <div className="main-screen-stack">
        <section className="main-daily-panel" aria-label="Wort des Tages">
          <DailyWords compact />
        </section>

        <section
          className="main-bottom-grid"
          aria-label="App Download und Lernbereiche"
        >
          <div className="main-app-download-panel" aria-label="App Download">
            <div className="main-panel-particles" aria-hidden="true">
              <span />
              <span />
              <span />
              <span />
              <span />
              <span />
            </div>

            <div className="main-app-qr-grid">
              <AppQrCard
                platform="iOS"
                device="iPhone / iPad"
                storeUrl={IOS_STORE_URL}
                variant="ios"
                icon={<FaApple />}
              />

              <AppQrCard
                platform="Android"
                device="Smartphone / Tablet"
                storeUrl={ANDROID_STORE_URL}
                variant="android"
                icon={<FaGooglePlay />}
              />
            </div>
          </div>

          <div className="main-learning-grid" aria-label="Lernbereiche">
            {learningCards.map((card) => (
              <button
                key={card.key}
                className={`main-learning-card ${card.className}`}
                type="button"
                aria-label={card.aria}
                onClick={() => handleCardClick(card.route)}
              >
                <div className="main-card-particles" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                </div>

                <div className="main-card-badge">
                  <span className="main-badge-glow" aria-hidden="true" />

                  <div className="main-badge-icon">{card.icon}</div>

                  <div className="main-badge-content">
                    <span className="main-badge-subtitle">
                      {card.subtitle}
                    </span>

                    <h1 className="main-badge-title">{card.title}</h1>

                    <p className="main-badge-description">
                      {card.description}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </section>
      </div>
    </main>
  );
};

export default MainScreen;